import { ChevronRight } from '@mui/icons-material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { Grid, Paper } from '@mui/material';
import { useEffect, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getJobStatsPostedOnJobBoards,
  ICareerBuilderJobPostingDTO,
  IJobBoard,
  postToCareerBuilder,
} from 'wisestep-api';
import {
  WsButton,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsRadioGroup,
  WsSelect,
  IUserDTO,
} from 'wisestep-base';

import NoDataFoundCard from '../../../../../../components/noDataFoundCard';
import { useUserContext } from '../../../../../../context';
import { getJobIndustries } from '../../../../../../features/jobs/helper/JobHelperService';
import CareerBuilder from '../../../../../../images/jobBoardsIcons/CareerBuilder.png';
import { useNotificationStore } from '../../../../../../utils/NotificationStore';
import JobPageParams from '../../../../types/JobPageParams';
import {
  getJobBoards,
  publishCreditsWarning,
  publishFailed,
  publishSuccess,
} from '../../helper/jobBoardHelper';
import SharedUserLists from '../SharedUserLists';

interface ICBCount {
  CareerBuilderCount: number;
}

const CareerBuilderAccount = () => {
  const [careerBuilderResponse, setCareerBuilderResponse] = useState<IJobBoard[]>([]);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const params = useParams<JobPageParams>();
  const jobId = params.jobId;
  const navigate = useNavigate();
  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const [emailsData, setEmailsData] = useState<IJobBoard[]>([]);
  const [jobStatsPostedOnJobBoard, setJobStatsPostedOnJobBoards] = useState<ICBCount>();

  const careerBuilderValues: ICareerBuilderJobPostingDTO = {
    jobIds: [jobId ?? ''],
    clientEmail: '',
    industryCode: [],
  };

  const methods = useForm<ICareerBuilderJobPostingDTO>();
  const { handleSubmit, watch, setValue } = methods;

  const clientEmails = careerBuilderResponse
    ?.filter(
      (account) =>
        account.jobBoardSharedUsers &&
        account.jobBoardSharedUsers?.filter(
          (sharedAccount) => sharedAccount.email == loggedInUser.email
        ).length > 0
    )
    .map((account) => ({
      label: account.username,
      value: account.username,
    }));

  const jobIndustries = getJobIndustries();
  const watchClientEmail = watch('clientEmail') ?? '';

  const getJobStatsPostedOnJobBoard = async () => {
    const response = await getJobStatsPostedOnJobBoards();
    setJobStatsPostedOnJobBoards(response.data);
  };

  useEffect(() => {
    const setCareerBuilderJobBoard = async () => {
      const allAccounts = await getJobBoards('CareerBuilder');
      const cbAccountDetails = allAccounts.filter(
        (account: IJobBoard) => account.jobBoardName === 'CareerBuilder'
      );
      setCareerBuilderResponse(cbAccountDetails);
      setValue('clientEmail', cbAccountDetails?.[0].username);
    };
    setCareerBuilderJobBoard();
    getJobStatsPostedOnJobBoard();
  }, [setValue]);

  useEffect(() => {
    const selectAccount: IJobBoard[] =
      careerBuilderResponse?.filter((account) => account.username === watchClientEmail) ?? [];
    setEmailsData(selectAccount);
  }, [careerBuilderResponse, watchClientEmail]);

  const onFormSubmit = async () => {
    const selectAccount: IJobBoard[] = emailsData;
    if (selectAccount.length > 0 && loggedInUser) {
      const maxPostingAvailableCount = selectAccount[0].jobBoardSharedUsers?.filter(
        (e) => e.email == loggedInUser.email
      )[0]?.maxPostingsPerMonth;

      if (
        !maxPostingAvailableCount ||
        (jobStatsPostedOnJobBoard &&
          jobStatsPostedOnJobBoard?.CareerBuilderCount >= maxPostingAvailableCount)
      ) {
        setNotification(publishCreditsWarning());
        return;
      }
    }
    try {
      careerBuilderValues.clientEmail = methods.getValues('clientEmail');
      careerBuilderValues.industryCode = methods.getValues('industryCode');
      if (jobId) await postToCareerBuilder(careerBuilderValues);
      setNotification(publishSuccess());
      navigate(`/jobs/${jobId}/publish`);
    } catch {
      setNotification(publishFailed());
    }
  };

  return (
    <Paper sx={{ pt: 2 }}>
      <Grid item xs={6} container columnGap={2}>
        <ChevronLeft onClick={() => navigate(`/jobs/${jobId}/publish`)} />
        <img src={CareerBuilder} alt="CareerBuilder" height="35" />
        <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
          Career Builder
        </WsTypography>
      </Grid>
      {clientEmails.length > 0 ? (
        <Grid>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
              <Paper sx={{ p: 2 }}>
                <Grid container p={3}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Choose an account *
                    </WsTypography>
                    <WsButton
                      variant="text"
                      size="large"
                      onClick={() => navigate('/job-boards/careerBuilder')}
                    >
                      Configure accounts
                    </WsButton>
                  </Grid>
                  {clientEmails && (
                    <Grid item xs={4} pl={1}>
                      <WsRadioGroup
                        name="clientEmail"
                        label=""
                        selectArray={clientEmails ?? []}
                        row={false}
                        size="medium"
                      />
                    </Grid>
                  )}
                </Grid>
                <Grid container p={3}>
                  <Grid item xs={3}>
                    <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.BLACK}>
                      Job Industry
                    </WsTypography>
                  </Grid>
                  <Grid item xs={4} pl={1}>
                    <WsSelect
                      name="industryCode"
                      label="Job Industry"
                      selectArray={jobIndustries}
                      size="medium"
                      required
                    />
                  </Grid>
                </Grid>
              </Paper>
              <Paper sx={{ p: 2 }}>
                <Grid container justifyContent="flex-end">
                  <WsButton
                    variant="contained"
                    type="submit"
                    size="large"
                    endIcon={<ChevronRight />}
                  >
                    Publish
                  </WsButton>
                </Grid>
              </Paper>
            </form>
          </FormProvider>
          <Grid container item xs={12}>
            {emailsData && emailsData.length > 0 && (
              <SharedUserLists
                value={emailsData[0].username}
                selectedAccount={emailsData}
                usedCount={jobStatsPostedOnJobBoard?.CareerBuilderCount ?? 0}
              />
            )}
          </Grid>
        </Grid>
      ) : (
        <NoDataFoundCard text="No Job posting credits allocated to your account, Contact Admin" />
      )}
    </Paper>
  );
};

export default CareerBuilderAccount;
